// index.js (Workflow Component)
import React, { useState, useContext, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import styled, { css } from "styled-components";
import { Icon as SemanticIcon } from "semantic-ui-react";
import Home from "./Home";
import RiskHome from "./Workflows/RiskHome";
import ROI from "./Workflows/ROIHome";
import Thematic from "./Workflows/Thematic";
import Change from "./Workflows/Change";
import NoraWorkflow from "./Workflows/Nora";
import { CopilotContext, CopilotProvider } from "./CopilotContext";
import DeleteModal from "new_components/organisms/DeleteModal";
import AnalyticsBreadcrumb from "ProtoType/NewAnalytics/AnalyticsBreadcrumb";

// HistorySidebar removed in favor of a modal-based history UI

const Workflow = () => {
  const [workflow, setWorkflow] = useState(null);
  const [history, setHistory] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isDeletePending, setIsDeletePending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    addChatWorkflowHistory,
    workflowData,
    deleteChatWorkflowHistory,
    setActiveWorkflow,
    clearMessages,
  } = useContext(CopilotContext);

  // For showing/hiding the Scroll-to-bottom button
  const scrollRef = useRef(null);
  const endOfMessagesRef = useRef(null);

  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  }, []);

  // Open/close history based on URL query (?history=1)
  useEffect(() => {
    const open = searchParams.get("history") === "1";
    setIsHistoryOpen(open);
  }, [searchParams]);

  // Sync workflow with URL param (?w=<type>) and rehydrate selected session (?wid=<id>)
  useEffect(() => {
    const w = searchParams.get("w");
    const wid = searchParams.get("wid");
    if (w && w !== workflow) {
      setWorkflow(w);
    }
    if (!w && workflow) {
      setWorkflow(null);
      setCurrentSession(null);
      setActiveWorkflow(null);
    }
    // if wid present, try to set current session from workflowData
    if (wid && workflowData?.results) {
      const full = workflowData.results.find((r) => String(r.id) === String(wid));
      if (full?.content?.[0]) {
        const normalized = { ...full.content[0], itemId: full.id, id: full.id };
        setCurrentSession(normalized);
        setActiveWorkflow({ id: normalized.id, ...normalized });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSaveSession = (sessionData) => {
    const newSession = { ...sessionData, created: Date.now() };
    setHistory((prev) => [newSession, ...prev]);
    // { name: '', content: [], params: {} }
    const payload = {
      name: newSession.type,
      content: [newSession],
      params: {},
    };
    addChatWorkflowHistory(payload);
  };

  const handleSelectSession = (session) => {
    // Normalize session payload to be the content[0] body that tools expect
    let normalized = session;
    if (Array.isArray(normalized)) normalized = normalized[0];

    // If a full workflow result object was passed, drill into content[0]
    if (normalized && normalized.content && Array.isArray(normalized.content)) {
      normalized = { ...normalized.content[0], itemId: normalized.id, id: normalized.id };
    }

    // If we only have a lightweight mapped item with itemId, re-lookup from source
    if (normalized && normalized.itemId && workflowData?.results) {
      const full = workflowData.results.find((r) => String(r.id) === String(normalized.itemId));
      if (full?.content?.[0]) {
        normalized = { ...full.content[0], itemId: full.id, id: full.id };
      }
    }

    // Final guard: if messages are missing, attempt best-effort recovery
    if (!normalized?.messages && workflowData?.results) {
      const fallback = workflowData.results.find((r) => String(r.id) === String(normalized?.id || normalized?.itemId));
      if (fallback?.content?.[0]) {
        normalized = { ...fallback.content[0], itemId: fallback.id, id: fallback.id };
      }
    }

    setCurrentSession(normalized);
    setActiveWorkflow({ id: normalized.id, ...normalized });

    // Write both workflow type and selected item id into URL for restoration
    const next = new URLSearchParams(window.location.search);
    next.set("w", normalized.type);
    if (normalized.id || normalized.itemId) {
      next.set("wid", String(normalized.id || normalized.itemId));
    }
    next.delete("history");
    setSearchParams(next, { replace: false });
    setIsSidebarOpen(false);
  };

  const handleGoHome = () => {
    setWorkflowAndURL(null);
    setCurrentSession(null);
    setIsSidebarOpen(true);
    setActiveWorkflow(null);
  };

  /**
   * 4) (REMOVED auto-scroll)
   *    Instead, we attach a scroll listener to show/hide the button.
   */
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const clientHeight = window.innerHeight;
      const scrollHeight = document.documentElement.scrollHeight;

      const threshold = 100;
      if (scrollHeight - scrollTop - clientHeight < threshold) {
        setShowScrollButton(false);
      } else {
        setShowScrollButton(true);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Manual scroll to bottom
  const scrollToBottom = () => {
    // Use window and scroll to bottom.
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (workflowData && workflowData?.results?.length > 0) {
      const history = workflowData?.results
        ?.filter((item) => item?.content?.length > 0)
        ?.flatMap((item) => {
          return { ...item.content[0], itemId: item.id, id: item.id };
        });

      // Order by created_at desc
      history.sort((a, b) => b.created - a.created);

      setHistory(history);
    } else {
      setHistory([]);
    }
  }, [workflowData]);

  // Auto-load most recent history item when workflow is selected
  useEffect(() => {
    const w = searchParams.get("w");
    const wid = searchParams.get("wid");

    // Only auto-load if:
    // 1. A workflow is selected (w)
    // 2. No specific session is selected (wid)
    // 3. History exists
    // 4. No current session is already set
    if (w && !wid && history.length > 0 && !currentSession) {
      // Find the most recent history item for this workflow type
      let mostRecent;

      if (w === "roi") {
        mostRecent = history.find(item => item.type === "roi");
      } else if (w === "risk") {
        mostRecent = history.find(item => item.type === "risk");
      } else if (w === "thematic") {
        mostRecent = history.find(item => item.type === "thematic");
      } else if (w === "change") {
        mostRecent = history.find(item => item.type === "change");
      }

      // Auto-select the most recent item
      if (mostRecent) {
        handleSelectSession(mostRecent);
      }
    }
  }, [workflow, history, currentSession, searchParams]);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const openHistory = () => {
    const next = new URLSearchParams(searchParams);
    next.set("history", "1");
    setSearchParams(next, { replace: false });
  };
  const closeHistory = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("history");
    setSearchParams(next, { replace: false });
  };

  const setWorkflowAndURL = (type, { clearSession } = {}) => {
    if (clearSession) {
      setCurrentSession(null);
      setActiveWorkflow(null);
      if (typeof clearMessages === "function") {
        try { clearMessages(); } catch (_) {}
      }
    }
    setWorkflow(type);
    const next = new URLSearchParams(searchParams);
    if (type) {
      next.set("w", type);
    } else {
      next.delete("w");
    }
    if (clearSession) {
      next.delete("wid");
    }
    setSearchParams(next, { replace: false });
  };

  // Calculate whether there's history for each workflow type
  // ROI/Risk can have type 'risk' or 'roi'
  const hasRoiHistory = history.some(item => item.type === "roi");
  const hasThematicHistory = history.some(item => item.type === "thematic");
  const hasChangeHistory = history.some(item => item.type === "change");

  // Filter history based on current workflow
  const getFilteredHistory = () => {
    if (workflow === "roi") {
      // Both 'roi' and 'risk' workflows show items with type 'risk' or 'roi'
      return history.filter(item => item.type === "roi");
    } else if (workflow === "thematic") {
      return history.filter(item => item.type === "thematic");
    } else if (workflow === "change") {
      return history.filter(item => item.type === "change");
    }
    // For other workflows or when no workflow is selected, show all history
    return history;
  };

  const filteredHistory = getFilteredHistory();

  let mainArea;
  if (!workflow) {
    mainArea = (
      <Home
        setWorkflow={(type) => {
          setWorkflowAndURL(type, { clearSession: true });
          setIsSidebarOpen(false);
        }}
      />
    );
  } else if (workflow === "risk") {
    mainArea = (
      <RiskHome
        goHome={() => {
          setWorkflow(null);
          setCurrentSession(null);
        }}
        initialSession={currentSession}
        onSaveSession={handleSaveSession}
      />
    );
  } else if (workflow === "nora") {
    mainArea = <NoraWorkflow />;
  } else if (workflow === "roi") {
    mainArea = (
      <ROI
        goHome={() => {
          setWorkflow(null);
          setCurrentSession(null);
        }}
        initialSession={currentSession}
        onSaveSession={handleSaveSession}
        openHistory={openHistory}
        hasHistory={hasRoiHistory}
      />
    );
  } else if (workflow === "thematic") {
    mainArea = (
      <Thematic
        goHome={() => {
          setWorkflow(null);
          setCurrentSession(null);
        }}
        initialSession={currentSession}
        onSaveSession={handleSaveSession}
        openHistory={openHistory}
        hasHistory={hasThematicHistory}
        selected={currentSession?.selectedFactors}
      />
    );
  } else if (workflow === "change") {
    mainArea = (
      <Change
        goHome={() => {
          setWorkflow(null);
          setCurrentSession(null);
        }}
        initialSession={currentSession}
        onSaveSession={handleSaveSession}
      />
    );
  }

  const handleBack = () => {
    setWorkflowAndURL(null, { clearSession: true });
    setCurrentSession(null);
  };

  return (
    <AppContainer ref={scrollRef}>
      <PageContent>
        <TopBar>
          <BreadcrumbWrapper>
            <AnalyticsBreadcrumb
              currentComponent={workflow ? `copilot-${workflow}` : "copilot"}
            />
          </BreadcrumbWrapper>
          <TopBarActions>
            {/* {workflow !== "nora" && (
              <HistoryButton onClick={openHistory}>
                <SemanticIcon name="history" />
                History
              </HistoryButton>
            )} */}
          </TopBarActions>
        </TopBar>

        <MainWorkspace>
          {mainArea}
          <div ref={endOfMessagesRef} />
        </MainWorkspace>
      </PageContent>

      {/* History Modal */}
      {isHistoryOpen && (
        <ModalOverlay onClick={closeHistory}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>History</ModalTitle>
              <CloseButton onClick={closeHistory} aria-label="Close history">
                <SemanticIcon name="close" />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              {isLoading ? (
                <EmptyState>Loading…</EmptyState>
              ) : filteredHistory.length === 0 ? (
                <EmptyState>No previous sessions found</EmptyState>
              ) : (
                <HistoryList>
                      {filteredHistory.map((item) => (
                    <SessionItem onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSelectSession(item); }}>
                      <InfoWrapper>
                        <SessionTitle>
                          {(() => {
                            if (item.type == "risk" && item?.selectedFactors?.type == "Culture") {
                              return `${item.selectedFactors.type} - ${item.type}`;
                            }
                            if (item.selectedFactors?.type == "Overview") {
                              return "Overview";
                            } else if (item.selectedFactors?.title) {
                              return `${item.selectedFactors?.title} - ${item.type}`;
                            } else if (item.type == "change") {
                              return `Change Analysis`;
                            } else {
                              return `${item.selectedFactors?.name} - ${item.type}`;
                            }
                          })()}
                        </SessionTitle>
                        <MetaRow>
                          <SessionDate>{new Date(item?.created).toLocaleDateString()}</SessionDate>
                          {item?.filters?.length > 0 && (
                            <Filters>
                              {item.filters.map((filter, idx) => (
                                <Filter key={idx}>{filter.name}</Filter>
                              ))}
                            </Filters>
                          )}
                        </MetaRow>
                      </InfoWrapper>
                      <DeleteWrapper
                        onClick={(event) => {
                          event.stopPropagation();
                          setIsDeletePending(item.itemId);
                        }}
                      >
                        <SemanticIcon name="trash" />
                      </DeleteWrapper>
                    </SessionItem>
                  ))}
                </HistoryList>
              )}
            </ModalBody>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* Delete confirm */}
      <DeleteModal
        isOpen={!!isDeletePending}
        onClose={() => setIsDeletePending(false)}
        onConfirm={() => {
          const deletedId = isDeletePending;
          setHistory((prev) => prev.filter((item) => item.itemId !== deletedId));
          deleteChatWorkflowHistory(deletedId);
          setIsDeletePending(false);

          const activeId = currentSession?.itemId ?? currentSession?.id;
          if (activeId && String(activeId) === String(deletedId)) {
            setCurrentSession(null);
            setActiveWorkflow(null);
            const next = new URLSearchParams(searchParams);
            next.delete("wid");
            setSearchParams(next, { replace: false });
          }
        }}
      />
    </AppContainer>
  );
};

export const WorkflowProvider = ({ children }) => {
  return (
    <CopilotProvider>
      <Workflow />
    </CopilotProvider>
  );
};

export default WorkflowProvider;

// Styled Components
const AppContainer = styled.div`
  min-height: 100vh;
  background-color: #ffffff;
`;
const BreadcrumbWrapper = styled.div`
  width: 100%;
`;
const PageContent = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 24px;
`;


// SidebarContainer removed (replaced by modal design)

// Removed ActionRow / back button in favor of breadcrumb navigation

// Top bar for page
const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HistoryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
  &:hover { background: #f9fafb; border-color: #d1d5db; }
`;

// Modal UI
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  width: 720px;
  max-height: 80vh;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  &:hover { background: #f3f4f6; color: #111827; }
`;

const ModalBody = styled.div`
  padding: 12px 20px 20px 20px;
  overflow: auto;
`;

const HistoryList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;
const SessionItem = styled.div`
  /* Use a tighter spacing approach */
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 6px;
  background: ${(props) => (props.isActive ? "#f8f9fa" : "#ffffff")};
  cursor: pointer;
  transition: background 0.2s ease;

  /* Subtle hover effect */
  &:hover {
    background: #f2f4f5;
  }
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SessionTitle = styled.div`
  font-size: 0.875rem; /* 14px equivalent if base is 16px */
  font-weight: 500;
  color: #2a3039;
  margin-bottom: 4px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px; /* space between date and filters */
`;

const SessionDate = styled.div`
  font-size: 0.75rem; /* 12px */
  color: #95a5a6;
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Filter = styled.span`
  background-color: #ecf0f1;
  color: #2c3e50;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
`;

const DeleteWrapper = styled.div`
  display: flex;
  align-items: center;
  color: #bdc3c7;

  &:hover {
    color: #c0392b;
  }
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: #95a5a6;
  font-size: 0.9rem;
`;

const SidebarToggleButton = styled.button`
  position: fixed;
  left: ${(props) => (props.isOpen ? "480px" : "200px")};
  top: 24px;
  width: 32px;
  height: 32px;
  background: #ffffff;
  border: none;
  border-radius: 0 6px 6px 0;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  z-index: 101;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(${(props) => (props.isOpen ? "0" : "0")});

  &:hover {
    background: #f8f9fa;
    transform: translateX(${(props) => (props.isOpen ? "4px" : "4px")});
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(44, 62, 80, 0.1);
  }
`;

const ToggleIcon = styled(SemanticIcon)`
  color: #2c3e50;
  margin: 0 !important;
`;

const MainWorkspace = styled.main`
  min-height: 50vh;
  padding-top: 4px;
  background-color: #ffffff;
`;


const ScrollToBottomButton = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #1678c2; /* or any color you prefer */
  color: #fff;
  padding: 10px 16px;
  border-radius: 24px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
